import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Plus,
  Search,
  Filter,
  Briefcase,
  Building,
  Calendar,
  ExternalLink,
  Edit3,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  TrendingUp,
  FileText,
  X,
  Check,
} from 'lucide-react';

import { logger } from '../utils/logger';
import { API } from '../utils/apiBase';


// Shared motion variants
const cardFadeIn = { opacity: 0, y: 20 };
const cardFadeInAnimate = { opacity: 1, y: 0 };
const modalEnter = { opacity: 0, scale: 0.95 };
const modalEnterAnimate = { opacity: 1, scale: 1 };

const STATUS_OPTIONS = [
  { value: 'applied', label: 'Applied', color: 'bg-[#3b82f6]', icon: Clock },
  { value: 'interviewing', label: 'Interviewing', color: 'bg-[#eab308]', icon: AlertCircle },
  { value: 'offered', label: 'Offered', color: 'bg-[#10b981]', icon: CheckCircle },
  { value: 'rejected', label: 'Rejected', color: 'bg-[#e11d48]', icon: XCircle },
  { value: 'ghosted', label: 'Ghosted', color: 'bg-[#6b7280]', icon: AlertCircle },
];

const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

// Stats Card Component
const StatsCard = ({ label, value, icon: Icon, color }) => (
  <div className="dashboard-card p-6">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6b7280]">{label}</span>
      <Icon size={20} className={color} />
    </div>
    <div className="text-3xl font-satoshi font-black text-[#0f1115]">{value}</div>
  </div>
);

// Application Card Component
const ApplicationCard = ({ application, onEdit, onDelete, onStatusChange }) => {
  const statusInfo = getStatusInfo(application.status);
  const StatusIcon = statusInfo.icon;

  return (
    <motion.div
      initial={cardFadeIn}
      animate={cardFadeInAnimate}
      className="dashboard-card p-6"
      data-testid={`application-${application.application_id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-satoshi text-lg font-bold text-[#0f1115]">{application.job_title}</h3>
          <p className="text-[#4b5563] flex items-center space-x-1">
            <Building size={14} />
            <span>{application.company}</span>
          </p>
        </div>
        <div className={`px-3 py-1 text-white text-sm font-medium flex items-center space-x-1 ${statusInfo.color}`}>
          <StatusIcon size={14} />
          <span>{statusInfo.label}</span>
        </div>
      </div>

      <div className="flex items-center space-x-4 text-sm text-[#6b7280] mb-4">
        <span className="flex items-center space-x-1">
          <Calendar size={14} />
          <span>{new Date(application.applied_date).toLocaleDateString()}</span>
        </span>
        {application.resume_title && (
          <span className="flex items-center space-x-1">
            <FileText size={14} />
            <span>{application.resume_title}</span>
          </span>
        )}
      </div>

      {application.notes && (
        <p className="text-sm text-[#4b5563] mb-4 line-clamp-2">{application.notes}</p>
      )}

      {application.job_description && (
        <details className="mb-4 text-sm" data-testid={`jd-details-${application.application_id}`}>
          <summary className="text-[#002fa7] cursor-pointer font-medium">View Job Description</summary>
          <p className="mt-2 text-[#4b5563] whitespace-pre-line line-clamp-6 bg-[#f9fafb] p-3 border border-[#e5e7eb]">
            {application.job_description}
          </p>
        </details>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-[#e5e7eb]">
        <div className="flex items-center space-x-2" data-testid="status-buttons">
          {STATUS_OPTIONS.map((status) => (
            <button
              key={status.value}
              onClick={(e) => { e.stopPropagation(); onStatusChange(application.application_id, status.value); }}
              className={`w-8 h-8 rounded-full ${status.color} ${application.status === status.value ? 'ring-2 ring-offset-2 ring-[#002fa7]' : 'opacity-50 hover:opacity-100'} transition-all flex items-center justify-center`}
              title={status.label}
              data-testid={`status-${status.value}-${application.application_id}`}
            >
              {application.status === status.value && <Check size={14} className="text-white" />}
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-2">
          {application.job_url && (
            <a href={application.job_url} target="_blank" rel="noopener noreferrer" className="p-2 text-[#6b7280] hover:text-[#002fa7]">
              <ExternalLink size={16} />
            </a>
          )}
          <button onClick={() => onEdit(application)} className="p-2 text-[#6b7280] hover:text-[#002fa7]">
            <Edit3 size={16} />
          </button>
          <button onClick={() => onDelete(application.application_id)} className="p-2 text-[#6b7280] hover:text-[#e11d48]">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Add/Edit Modal Component
const ApplicationModal = ({ isOpen, onClose, onSave, application, resumes }) => {
  const [formData, setFormData] = useState({
    company: '',
    job_title: '',
    job_url: '',
    resume_id: '',
    job_description: '',
    notes: '',
    applied_date: new Date().toISOString().split('T')[0],
    status: 'applied',
    follow_up_date: '',
  });

  useEffect(() => {
    if (application) {
      setFormData({
        company: application.company || '',
        job_title: application.job_title || '',
        job_url: application.job_url || '',
        resume_id: application.resume_id || '',
        job_description: application.job_description || '',
        notes: application.notes || '',
        applied_date: application.applied_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        status: application.status || 'applied',
        follow_up_date: application.follow_up_date?.split('T')[0] || '',
      });
    } else {
      setFormData({
        company: '',
        job_title: '',
        job_url: '',
        resume_id: '',
        job_description: '',
        notes: '',
        applied_date: new Date().toISOString().split('T')[0],
        status: 'applied',
        follow_up_date: '',
      });
    }
  }, [application, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <motion.div
        initial={modalEnter}
        animate={modalEnterAnimate}
        className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#e5e7eb] flex items-center justify-between">
          <h2 className="font-satoshi text-xl font-bold text-[#0f1115]">
            {application ? 'Edit Application' : 'Add Application'}
          </h2>
          <button onClick={onClose} className="text-[#6b7280] hover:text-[#0f1115]">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">Company *</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
                placeholder="Google"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">Job Title *</label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
                placeholder="Senior Software Engineer"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0f1115] mb-2">Job URL</label>
            <input
              type="url"
              value={formData.job_url}
              onChange={(e) => setFormData({ ...formData, job_url: e.target.value })}
              className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
              placeholder="https://careers.google.com/..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">Applied Date</label>
              <input
                type="date"
                value={formData.applied_date}
                onChange={(e) => setFormData({ ...formData, applied_date: e.target.value })}
                className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0f1115] mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0f1115] mb-2">Resume Used</label>
            <select
              value={formData.resume_id}
              onChange={(e) => setFormData({ ...formData, resume_id: e.target.value })}
              className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
            >
              <option value="">None</option>
              {resumes.map((resume) => (
                <option key={resume.resume_id} value={resume.resume_id}>{resume.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0f1115] mb-2">Job Description</label>
            <textarea
              value={formData.job_description}
              onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
              rows={5}
              className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115] text-sm"
              placeholder="Paste the job description here to keep it with your application..."
              data-testid="job-description-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0f1115] mb-2">Follow-up Date</label>
            <input
              type="date"
              value={formData.follow_up_date}
              onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
              className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0f1115] mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-[#e5e7eb] bg-white text-[#0f1115]"
              placeholder="Any notes about this application..."
            />
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-outline" data-testid="modal-cancel">Cancel</button>
            <button type="submit" className="btn-primary" data-testid="modal-submit">
              {application ? 'Update' : 'Add'} Application
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const ApplicationTracker = () => {
  const [applications, setApplications] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, this_week: 0, this_month: 0, response_rate: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const apiRef = useRef(API);

  const fetchData = useCallback(async () => {
    try {
      const [appsRes, statsRes, resumesRes] = await Promise.all([
        axios.get(`${apiRef.current}/applications`, { withCredentials: true }),
        axios.get(`${apiRef.current}/applications/stats`, { withCredentials: true }),
        axios.get(`${apiRef.current}/resumes`, { withCredentials: true }),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
      setResumes(resumesRes.data);
    } catch (err) {
      logger.error('Failed to fetch application data:', err.message);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = useCallback(async (formData) => {
    try {
      if (editingApp) {
        await axios.put(`${apiRef.current}/applications/${editingApp.application_id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${apiRef.current}/applications`, formData, { withCredentials: true });
      }
      fetchData();
      setModalOpen(false);
      setEditingApp(null);
    } catch (err) {
      logger.error('Failed to save application:', err.message);
    }
  }, [editingApp, fetchData]);

  const handleDelete = useCallback(async (applicationId) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await axios.delete(`${apiRef.current}/applications/${applicationId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      logger.error('Failed to delete application:', err.message);
    }
  }, [fetchData]);

  const handleStatusChange = useCallback(async (applicationId, newStatus) => {
    try {
      await axios.put(`${apiRef.current}/applications/${applicationId}`, { status: newStatus }, { withCredentials: true });
      fetchData();
    } catch (err) {
      logger.error('Failed to update status:', err.message);
    }
  }, [fetchData]);

  const handleEdit = (app) => {
    setEditingApp(app);
    setModalOpen(true);
  };

  const filteredApps = applications
    .filter((app) => {
      const matchesSearch =
        app.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.job_title.toLowerCase().includes(searchTerm.toLowerCase());
      if (filterStatus === 'all') return matchesSearch;
      return matchesSearch && app.status === filterStatus;
    });

  return (
    <div className="min-h-screen bg-[#f4f5f7] pt-20" data-testid="application-tracker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-satoshi text-3xl font-black tracking-tight text-[#0f1115]">
              Application Tracker
            </h1>
            <p className="text-[#4b5563] mt-1">Track your job applications and follow-ups</p>
          </div>
          <button
            onClick={() => { setEditingApp(null); setModalOpen(true); }}
            className="mt-4 md:mt-0 btn-primary inline-flex items-center space-x-2"
            data-testid="add-application-button"
          >
            <Plus size={20} />
            <span>Add Application</span>
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <StatsCard label="Total" value={stats.total} icon={Briefcase} color="text-[#002fa7]" />
          <StatsCard label="This Week" value={stats.this_week} icon={Calendar} color="text-[#10b981]" />
          <StatsCard label="This Month" value={stats.this_month} icon={TrendingUp} color="text-[#eab308]" />
          <StatsCard label="Response Rate" value={`${stats.response_rate}%`} icon={CheckCircle} color="text-[#10b981]" />
          <StatsCard label="Interviewing" value={stats.by_status?.interviewing || 0} icon={AlertCircle} color="text-[#eab308]" />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
            <input
              type="text"
              placeholder="Search by company or job title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] bg-white text-[#0f1115]"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b7280]" size={20} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-12 pr-8 py-3 border border-[#e5e7eb] bg-white text-[#0f1115] appearance-none cursor-pointer"
            >
              <option value="all">All Status</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Applications Grid */}
        {loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={`skeleton-${i}`} className="dashboard-card p-6 animate-pulse">
                <div className="h-6 bg-[#e5e7eb] w-3/4 mb-4"></div>
                <div className="h-4 bg-[#e5e7eb] w-1/2"></div>
              </div>
            ))}
          </div>
        )}
        {!loading && filteredApps.length === 0 && (
          <div className="text-center py-16">
            <Briefcase size={64} className="mx-auto text-[#d1d5db] mb-4" />
            <h3 className="font-satoshi text-xl font-bold text-[#0f1115] mb-2">
              {searchTerm || filterStatus !== 'all' ? 'No applications found' : 'No applications yet'}
            </h3>
            <p className="text-[#6b7280] mb-6">
              {searchTerm || filterStatus !== 'all' ? 'Try adjusting your filters' : 'Start tracking your job applications'}
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button onClick={() => setModalOpen(true)} className="btn-primary inline-flex items-center space-x-2">
                <Plus size={20} />
                <span>Add First Application</span>
              </button>
            )}
          </div>
        )}
        {!loading && filteredApps.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map((app) => (
              <ApplicationCard
                key={app.application_id}
                application={app}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onStatusChange={handleStatusChange}
              />
            ))}
          </div>
        )}

        {/* Modal */}
        <ApplicationModal
          isOpen={modalOpen}
          onClose={() => { setModalOpen(false); setEditingApp(null); }}
          onSave={handleSave}
          application={editingApp}
          resumes={resumes}
        />
      </div>
    </div>
  );
};

export default ApplicationTracker;
